import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { isNil } from 'lodash';
import { Box, Divider, SxProps, Theme } from '@mui/material';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import type { ApiOrganization, ApiOrgOverview } from '@agunity/api-v4';

import AgTypography from '../../../stories/Styles/AgTypography';
import AgChip from '../../../stories/Chips/AgChip';

import { AgColor } from '../../../stories/Styles/AgColor';
import { useAppSelector } from '../../../redux/hooks';
import { ExtendedRenderTree } from '../../../redux/slices/global';
import { findAncestors } from '../../../stories/Utils/TreeUtils';

const RightSidebar = ({
  orgData,
  summaryData,
  totalProfilesStyles,
}: {
  orgData?: ApiOrganization;
  summaryData?: ApiOrgOverview;
  totalProfilesStyles?: SxProps<Theme>;
}) => {
  const messages = useIntl().formatMessage;

  const orgTree = useAppSelector((state) => state.global.orgTree);

  const orgPlacement = useMemo(() => {
    const parentOrgId = orgData?.relationships
      ? orgData?.relationships?.[0]?.orgBId
      : undefined;
    const ancestors = findAncestors(
      orgTree as ExtendedRenderTree,
      String(parentOrgId)
    );
    return [...(ancestors?.map((x) => x.name) ?? []), orgData?.name];
  }, [orgTree, orgData?.relationships, orgData?.name]);

  const totalMembers = summaryData?.totalMembers;
  const totalStaff = summaryData?.totalStaff;

  return (
    <Box
      sx={{
        boxShadow: '0px 1px 2px 0px #0000000D',
        backgroundColor: AgColor.grey0,
        borderRadius: '8px',
        border: `1px solid ${AgColor.grey3}`,
        padding: '12px 16px',
        width: 184,
        alignSelf: 'start',
      }}
    >
      <AgTypography
        contentStyle='AP.Assistive.Text.SemiBold'
        style={{
          fontWeight: '600',
          color: AgColor.grey6,
        }}
      >
        {messages({ id: 'TOTAL_PROFILES' })}
      </AgTypography>
      <Box mt={1.5} sx={totalProfilesStyles}>
        <AgChip
          customBadgeContent={isNil(totalMembers) ? '-' : undefined}
          badgeContent={isNil(totalMembers) ? 0 : totalMembers}
          badgeOn
          icon_badge_side='left'
          label='Members'
          onClick={() => {}}
          type='members'
        />
        <AgChip
          customBadgeContent={isNil(totalStaff) ? '-' : undefined}
          badgeContent={isNil(totalStaff) ? 0 : totalStaff}
          badgeOn
          icon_badge_side='left'
          label='Staff'
          onClick={() => {}}
          type='staff'
        />
      </Box>
      <Divider sx={{ margin: '16px 0' }} />
      <Box mt={1.5}>
        <AgTypography
          contentStyle='AP.Assistive.Text.SemiBold'
          style={{
            fontWeight: '600',
            color: AgColor.grey6,
          }}
        >
          {messages({ id: 'ORGANIZATION_PLACEMENT' })}
        </AgTypography>
        {orgPlacement?.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              ml: `${(index > 1 ? 10 : 0) * (index - 1)}px`,
            }}
          >
            {index !== 0 && (
              <SubdirectoryArrowRightIcon
                sx={{ color: AgColor.grey5, height: 15, width: 16 }}
              />
            )}
            <Box
              flex={1}
              sx={{
                '&:not(:first-child) > div': {
                  width: '100%',
                },
              }}
            >
              <AgChip onClick={() => {}} type='organizations' label={item} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RightSidebar;
